import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { usePermissions } from "@/hooks/usePermissions";
import axios from "axios";
import { toast } from "sonner";
import {
  Wrench,
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  Phone,
  EnvelopeSimple,
  MapPin,
  ClockCounterClockwise,
  Car
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const TYPE_LABELS = {
  carrosserie: "Carrosserie",
  debosselage: "Debosselage",
  jantes: "Jantes",
  mecanique: "Mecanique",
  nettoyage: "Nettoyage / Detailing",
  controle_technique: "Controle technique",
  autre: "Autre",
};

const TYPE_COLORS = {
  carrosserie: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  debosselage: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  jantes: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  mecanique: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  nettoyage: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  controle_technique: "bg-slate-100 text-slate-700 dark:bg-slate-700/40 dark:text-slate-300",
  autre: "bg-zinc-100 text-zinc-700 dark:bg-zinc-700/40 dark:text-zinc-300",
};

const INITIAL_FORM = { nom: "", type_prestation: "carrosserie", telephone: "", email: "", adresse: "", notes: "" };

const Prestataires = () => {
  const { can } = usePermissions();
  const [prestataires, setPrestataires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [prepTypes, setPrepTypes] = useState([]);
  const [historique, setHistorique] = useState(null);
  const [showHistorique, setShowHistorique] = useState(false);

  const canCreate = can("preparation", "create");
  const canUpdate = can("preparation", "update");
  const canDelete = can("preparation", "delete");

  const fetchData = useCallback(async () => {
    try {
      const [prestRes, typesRes] = await Promise.all([
        axios.get(`${API}/prestataires`),
        axios.get(`${API}/preparation-types`),
      ]);
      setPrestataires(prestRes.data || []);
      setPrepTypes(typesRes.data || []);
    } catch {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openHistorique = async (prestId) => {
    try {
      const res = await axios.get(`${API}/prestataires/${prestId}/historique`);
      setHistorique(res.data);
      setShowHistorique(true);
    } catch { toast.error("Erreur chargement historique"); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()) { toast.error("Nom requis"); return; }
    try {
      await axios.post(`${API}/prestataires`, form);
      toast.success("Prestataire cree");
      setDialogOpen(false);
      setForm(INITIAL_FORM);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editDialog) return;
    try {
      await axios.put(`${API}/prestataires/${editDialog.prestataire_id}`, form);
      toast.success("Prestataire mis a jour");
      setEditDialog(null);
      setForm(INITIAL_FORM);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/prestataires/${id}`);
      toast.success("Prestataire supprime");
      fetchData();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const openEdit = (p) => {
    setForm({ nom: p.nom, type_prestation: p.type_prestation, telephone: p.telephone || "", email: p.email || "", adresse: p.adresse || "", notes: p.notes || "" });
    setEditDialog(p);
  };

  const filtered = prestataires.filter(p => {
    const matchSearch = !searchTerm || p.nom.toLowerCase().includes(searchTerm.toLowerCase()) || (p.email || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = typeFilter === "all" || p.type_prestation === typeFilter;
    return matchSearch && matchType;
  });

  const typeOptions = prepTypes.length > 0
    ? prepTypes.map(t => ({ value: t.type_id, label: t.label }))
    : Object.entries(TYPE_LABELS).map(([k, v]) => ({ value: k, label: v }));

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner h-10 w-10"></div></div>;
  }

  const formFieldsJsx = (isEdit) => (
    <div className="space-y-4">
      <div>
        <Label className="form-label">Nom *</Label>
        <Input data-testid="prest-nom" value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} placeholder="Ex: AutoClean SRL" />
      </div>
      <div>
        <Label className="form-label">Type de prestation</Label>
        <Select value={form.type_prestation} onValueChange={v => setForm({ ...form, type_prestation: v })}>
          <SelectTrigger data-testid="prest-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            {typeOptions.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="form-label">Telephone</Label>
          <Input data-testid="prest-tel" value={form.telephone} onChange={e => setForm({ ...form, telephone: e.target.value })} placeholder="0477..." />
        </div>
        <div>
          <Label className="form-label">Email</Label>
          <Input data-testid="prest-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="info@..." />
        </div>
      </div>
      <div>
        <Label className="form-label">Adresse</Label>
        <Input data-testid="prest-adresse" value={form.adresse} onChange={e => setForm({ ...form, adresse: e.target.value })} placeholder="Rue..." />
      </div>
      <div>
        <Label className="form-label">Notes</Label>
        <Input data-testid="prest-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes internes..." />
      </div>
      <Button type="submit" className="btn-primary w-full" data-testid={isEdit ? "prest-update-btn" : "prest-create-btn"}>
        {isEdit ? "Mettre a jour" : "Creer le prestataire"}
      </Button>
    </div>
  );

  return (
    <div data-testid="prestataires-page" className="animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Prestataires</h1>
          <p className="page-subtitle">{prestataires.length} prestataire(s) enregistre(s)</p>
        </div>
        <div className="page-actions">
          {canCreate && (
            <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setForm(INITIAL_FORM); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-prestataire-btn" className="btn-primary">
                  <Plus size={18} className="mr-2" /> Nouveau prestataire
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader><DialogTitle>Nouveau prestataire</DialogTitle></DialogHeader>
                <form onSubmit={handleCreate}>{formFieldsJsx(false)}</form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="search-prestataires" className="pl-10" placeholder="Rechercher..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48" data-testid="filter-type-prest">
            <SelectValue placeholder="Tous les types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            {typeOptions.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Cards grid */}
      {filtered.length === 0 ? (
        <div className="empty-state">
          <Wrench size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="empty-state-title">Aucun prestataire</h3>
          <p className="empty-state-description">Ajoutez vos premiers prestataires pour gerer les preparations.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(p => (
            <div key={p.prestataire_id} data-testid={`prest-card-${p.prestataire_id}`}
              className="card p-5 hover:shadow-md transition-all duration-200 group">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm truncate">{p.nom}</h3>
                  <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${TYPE_COLORS[p.type_prestation] || TYPE_COLORS.autre}`}>
                    {TYPE_LABELS[p.type_prestation] || p.type_prestation}
                  </span>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openHistorique(p.prestataire_id)} className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500" data-testid={`historique-prest-${p.prestataire_id}`} title="Historique">
                    <ClockCounterClockwise size={16} />
                  </button>
                  {canUpdate && (
                    <button onClick={() => openEdit(p)} className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500" data-testid={`edit-prest-${p.prestataire_id}`}>
                      <PencilSimple size={16} />
                    </button>
                  )}
                  {canDelete && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-500 hover:text-red-600" data-testid={`delete-prest-${p.prestataire_id}`}>
                          <Trash size={16} />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Supprimer {p.nom} ?</AlertDialogTitle><AlertDialogDescription>Le prestataire sera desactive.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(p.prestataire_id)} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
              <div className="space-y-1.5 text-xs text-slate-500 dark:text-slate-400">
                {p.telephone && (
                  <div className="flex items-center gap-2"><Phone size={14} /><span>{p.telephone}</span></div>
                )}
                {p.email && (
                  <div className="flex items-center gap-2"><EnvelopeSimple size={14} /><span className="truncate">{p.email}</span></div>
                )}
                {p.adresse && (
                  <div className="flex items-center gap-2"><MapPin size={14} /><span className="truncate">{p.adresse}</span></div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(v) => { if (!v) { setEditDialog(null); setForm(INITIAL_FORM); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Modifier le prestataire</DialogTitle></DialogHeader>
          <form onSubmit={handleUpdate}>{formFieldsJsx(true)}</form>
        </DialogContent>
      </Dialog>

      {/* Historique Dialog */}
      <Dialog open={showHistorique} onOpenChange={setShowHistorique}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Historique - {historique?.prestataire?.nom}</DialogTitle></DialogHeader>
          {historique && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-xl font-bold text-blue-600">{historique.stats.total_interventions}</p>
                  <p className="text-xs text-slate-500">Interventions</p>
                </div>
                <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                  <p className="text-xl font-bold text-emerald-600">{historique.stats.terminees}</p>
                  <p className="text-xs text-slate-500">Terminees</p>
                </div>
                <div className="text-center p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                  <p className="text-xl font-bold text-amber-600">{historique.stats.en_cours}</p>
                  <p className="text-xs text-slate-500">En cours</p>
                </div>
                <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xl font-bold text-slate-700 dark:text-slate-200">{(historique.stats.ca_total || 0).toLocaleString("fr-BE", { minimumFractionDigits: 2 })} EUR</p>
                  <p className="text-xs text-slate-500">CA total</p>
                </div>
              </div>
              {historique.interventions?.length > 0 ? (
                <div className="space-y-2">
                  {historique.interventions.map(t => (
                    <div key={t.task_id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700">
                      <Car size={16} className="text-blue-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{t.vehicle_nom || "Vehicule"} <span className="text-slate-400 font-normal">({t.vehicle_ref})</span></p>
                        <p className="text-xs text-slate-500">{t.type_prestation} | {t.date_debut ? new Date(t.date_debut).toLocaleDateString("fr-BE") : "N/A"}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${t.statut === 'termine' ? 'bg-emerald-100 text-emerald-700' : t.statut === 'en_cours' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{t.statut}</span>
                      <span className="text-sm font-mono text-slate-700 dark:text-slate-300">{(t.prix || 0).toLocaleString("fr-BE")} EUR</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-sm text-slate-400 py-4">Aucune intervention enregistree</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Prestataires;
